import { RequestParams } from '~/Api/generated-api/api';
import {
  mapDetectorPolicyLimits,
  mapLegacyDetectorLimits,
} from '~/services/toolPolicy/limits/detectorWordLimits';
import {
  DetectorPolicyToolKey,
  DetectorWordsLimits,
  LegacyDetectorLimitsResponse,
  ToolPolicyResponse,
} from '~/services/toolPolicy/types';

type LoadToolPolicyResponse = (
  params: RequestParams,
) => Promise<ToolPolicyResponse | null>;

export class ToolPolicyService {
  private pendingToolPolicyResponse: Promise<ToolPolicyResponse | null> | null =
    null;
  private latestToolPolicyResponse: ToolPolicyResponse | null = null;
  private resetVersion = 0;

  private loadPolicy: LoadToolPolicyResponse;

  private isPolicyEnabled: () => boolean;

  constructor({
    isPolicyEnabled = (): boolean => true,
    loadPolicy,
  }: {
    isPolicyEnabled?: () => boolean;
    loadPolicy: LoadToolPolicyResponse;
  }) {
    this.isPolicyEnabled = isPolicyEnabled;
    this.loadPolicy = loadPolicy;
  }

  reset(): void {
    // Clears policy context when auth/profile/tool route state is refreshed.
    this.resetVersion += 1;
    this.pendingToolPolicyResponse = null;
    this.latestToolPolicyResponse = null;
  }

  getLatestPolicyResponse(): ToolPolicyResponse | null {
    return this.latestToolPolicyResponse;
  }

  loadPolicyResponse(
    params: RequestParams,
  ): Promise<ToolPolicyResponse | null> {
    if (!this.isPolicyEnabled()) {
      return Promise.resolve(null);
    }

    return this.loadSharedPolicyResponse(params);
  }

  private loadSharedPolicyResponse(
    params: RequestParams,
  ): Promise<ToolPolicyResponse | null> {
    if (!this.pendingToolPolicyResponse) {
      const loadVersion = this.resetVersion;

      this.pendingToolPolicyResponse = this.loadPolicy(params)
        .then((policy) => {
          if (loadVersion === this.resetVersion) {
            this.latestToolPolicyResponse = policy;
          }

          return policy;
        })
        .catch(() => null)
        .finally(() => {
          if (loadVersion === this.resetVersion) {
            this.pendingToolPolicyResponse = null;
          }
        });
    }

    return this.pendingToolPolicyResponse;
  }

  async loadPolicyManaged(params: RequestParams): Promise<boolean> {
    return (
      (await this.loadSharedPolicyResponse(params))?.policy_managed === true
    );
  }

  async mapDetectorToolWordsResponse(
    params: RequestParams,
    toolKey: DetectorPolicyToolKey,
    legacyTool: LegacyDetectorLimitsResponse,
  ): Promise<DetectorWordsLimits & { policy_managed: boolean }> {
    const policy = await this.loadPolicyResponse(params);

    const limits =
      mapDetectorPolicyLimits(policy, toolKey) ||
      mapLegacyDetectorLimits(legacyTool);

    return {
      ...limits,
      policy_managed: policy?.policy_managed === true,
    };
  }
}
